import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./bulk-upload-popup.scss";

export type UploadOperationType =
  | "Provider Registration"
  | "Provider Services"
  | "Planner"
  | "Service"
  | "Subservice"
  | "Merchandise"
  | "Provider";

export const providerUploadSlots = [
  { key: "providerRegistration", label: "Provider Registrations" },
  { key: "providerService", label: "Provider Services" },
  { key: "service", label: "Services" },
  { key: "subservice", label: "Subservices" },
  { key: "merchandise", label: "Merchandise Items" },
] as const;

export type ProviderUploadSlotKey = (typeof providerUploadSlots)[number]["key"];
export type ProviderSelectedFiles = Record<ProviderUploadSlotKey, File | null>;

export interface BulkUploadSubmitPayload {
  operationType: UploadOperationType;
  providerFiles: ProviderSelectedFiles;
  plannerFile: File | null;
}

interface BulkUploadPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: BulkUploadSubmitPayload) => void;
}

const acceptedExtensions = [".csv", ".xlsx"];


const initialProviderSelectedFiles: ProviderSelectedFiles = {
  providerRegistration: null,
  providerService: null,
  service: null,
  merchandise: null,
  subservice: null,
};

const providerTemplates = [
  {
    title: "Provider Registrations",
    xlsxUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/provider_registrations.xlsx",
    csvUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Provider_Registrations.csv"
  },
  {
    title: "Provider Services",
    xlsxUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/provider_services.xlsx",
    csvUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Provider_Services.csv"
  },
  {
    title: "Services",
    xlsxUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Services.xlsx",
    csvUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Services+1.csv"
  },
  {
    title: "Sub Services",
    xlsxUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Sub_Services.xlsx",
    csvUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Sub_Services.csv"
  },
  {
    title: "Merchandises",
    xlsxUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Merchandises.xlsx",
    csvUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Merchandises+1.csv"
  },
] as const;

const plannerTemplates = [
  {
    title: "Planners",
    xlsxUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Planners.xlsx",
    csvUrl: "https://afp-memorial-qa-be.s3.us-east-1.amazonaws.com/uploads/BulkUpload/samples/Planners+1.csv"
  },
] as const;

const BulkUploadPopup: React.FC<BulkUploadPopupProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [operationType, setOperationType] =
    useState<UploadOperationType>("Provider");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [providerSelectedFiles, setProviderSelectedFiles] =
    useState<ProviderSelectedFiles>(initialProviderSelectedFiles);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const providerInputRefs = useRef<Record<ProviderUploadSlotKey, HTMLInputElement | null>>({
    providerRegistration: null,
    providerService: null,
    service: null,
    merchandise: null,
    subservice: null,
  });

  useEffect(() => {
    if (open) {
      setOperationType("Provider");
      setSelectedFiles([]);
      setProviderSelectedFiles(initialProviderSelectedFiles);
      setErrorMessage("");
    }
  }, [open]);

  const helperText = useMemo(() => {
    if (operationType === "Provider") {
      return "Upload Provider, Services, Subservices, and Merchandise Items files separately.";
    }
    return "Planner upload accepts exactly 1 file.";
  }, [operationType]);

  const getIsAllowedFile = (file: File) => {
    const fileName = file.name.toLowerCase();
    return acceptedExtensions.some((ext) => fileName.endsWith(ext));
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setErrorMessage("");

    const filteredByType = files.filter((file) => getIsAllowedFile(file));

    if (filteredByType.length !== files.length) {
      setErrorMessage("Only CSV and XLSX files are supported.");
    }

    if (operationType === "Planner") {
      setSelectedFiles(filteredByType.slice(0, 1));
      event.target.value = "";
      return;
    }

    event.target.value = "";
  };

  const handleProviderFileSelection = (
    slotKey: ProviderUploadSlotKey,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0] || null;
    setErrorMessage("");

    if (selectedFile && !getIsAllowedFile(selectedFile)) {
      setErrorMessage("Only CSV and XLSX files are supported.");
      event.target.value = "";
      return;
    }

    setProviderSelectedFiles((currentFiles) => ({
      ...currentFiles,
      [slotKey]: selectedFile,
    }));

    event.target.value = "";
  };

  const handleSubmit = () => {
    if (operationType === "Provider") {
      const providerFiles = providerUploadSlots
        .map((slot) => providerSelectedFiles[slot.key])
        .filter((file): file is File => Boolean(file));

      if (providerFiles.length < 1) {
        setErrorMessage("For Provider, select at least one file.");
        return;
      }

      onSubmit({
        operationType,
        providerFiles: providerSelectedFiles,
        plannerFile: null,
      });
      return;
    }

    if (operationType === "Planner") {
      if (selectedFiles.length !== 1) {
        setErrorMessage("For Planner, select exactly 1 file.");
        return;
      }

      onSubmit({
        operationType,
        providerFiles: initialProviderSelectedFiles,
        plannerFile: selectedFiles[0],
      });
      return;
    }
  };

  const openPlannerFileSelector = () => {
    const input = fileInputRef.current;
    if (input) {
      input.value = "";
      input.click();
    }
  };

  const openProviderFileSelector = (slotKey: ProviderUploadSlotKey) => {
    const input = providerInputRefs.current[slotKey];
    if (input) {
      input.value = "";
      input.click();
    }
  };



  const visibleTemplates =
    operationType === "Provider" ? providerTemplates : plannerTemplates;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" className="bulk-upload-popup-dialog">
      <Box className="bulk-upload-popup-header">
        <Typography className="bulk-upload-popup-title">Import Bulk Records</Typography>
        <IconButton onClick={onClose} className="bulk-upload-popup-close-btn" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className="bulk-upload-popup-content">
        <Box className="bulk-upload-popup-inner">
          <Typography className="bulk-upload-popup-description">
            Upload an XLSX or CSV file with the correct template in order to
            successfully import records in bulk.
          </Typography>

          <Box className="bulk-upload-tabs-container">
            <Tabs
              value={operationType === "Provider" ? 0 : 1}
              onChange={(_, newVal) => {
                setOperationType(newVal === 0 ? "Provider" : "Planner");
                setSelectedFiles([]);
                setProviderSelectedFiles(initialProviderSelectedFiles);
                setErrorMessage("");
              }}
              variant="fullWidth"
            >
              <Tab label="Provider" />
              <Tab label="Planner" />
            </Tabs>
          </Box>

          <Box className="bulk-upload-tab-content" key={operationType}>
            {operationType === "Provider" ? (
              <Box className="bulk-upload-grid">
                {providerUploadSlots.map((slot) => {
                  const hasFile = !!providerSelectedFiles[slot.key];
                  return (
                    <Box
                      key={slot.key}
                      className={`bulk-upload-card ${hasFile ? "has-file" : ""}`}
                      onClick={() => openProviderFileSelector(slot.key)}
                    >
                      {hasFile ? <CheckCircleIcon className="success-icon" /> : <CloudUploadIcon className="upload-icon" />}
                      <Typography className="upload-label">{slot.label}</Typography>
                      {hasFile ? (
                        <Typography className="file-name" title={providerSelectedFiles[slot.key]?.name}>
                          {providerSelectedFiles[slot.key]?.name}
                        </Typography>
                      ) : (
                        <Typography className="select-text">Click to browse file</Typography>
                      )}
                      <input
                        id={`bulk-upload-file-input-${slot.key}`}
                        ref={(element) => {
                          providerInputRefs.current[slot.key] = element;
                        }}
                        type="file"
                        onChange={(event) => handleProviderFileSelection(slot.key, event)}
                        accept=".csv,.xlsx"
                        className="bulk-upload-popup-file-input"
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box className="bulk-upload-grid">
                <Box
                  className={`bulk-upload-card planner-upload-card ${selectedFiles.length > 0 ? "has-file" : ""}`}
                  onClick={openPlannerFileSelector}
                >
                  {selectedFiles.length > 0 ? <CheckCircleIcon className="success-icon" /> : <CloudUploadIcon className="upload-icon" />}
                  <Typography className="upload-label">Planner Records</Typography>
                  {selectedFiles.length > 0 ? (
                    <Typography className="file-name" title={selectedFiles[0]?.name}>
                      {selectedFiles[0]?.name}
                    </Typography>
                  ) : (
                    <Typography className="select-text">Click to browse file</Typography>
                  )}
                  <input
                    id="bulk-upload-file-input"
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelection}
                    accept=".csv,.xlsx"
                    className="bulk-upload-popup-file-input"
                  />
                </Box>
              </Box>
            )}
          </Box>

          <Typography variant="caption" className="bulk-upload-popup-limit-text">
            {helperText}
          </Typography>

          {!!errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

          <Box className="bulk-upload-templates-section">
            <Typography className="bulk-upload-templates-header">
              Download Sample Templates
            </Typography>
            <Box className="bulk-upload-templates-list">
              {visibleTemplates.map((template) => (
                <Box className="bulk-upload-template-row" key={template.title}>
                  <Box className="bulk-upload-template-info">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="template-file-icon"
                    >
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <line x1="10" y1="9" x2="8" y2="9" />
                    </svg>
                    <Typography className="bulk-upload-template-name">
                      {template.title}
                    </Typography>
                  </Box>
                  <Box className="bulk-upload-template-actions">
                    <a
                      href={template.xlsxUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="template-btn xlsx"
                    >
                      XLSX
                    </a>
                    <a
                      href={template.csvUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="template-btn csv"
                    >
                      CSV
                    </a>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

        </Box>
      </DialogContent>
      <Box className="bulk-upload-popup-footer">
        <Button onClick={onClose} className="bulk-upload-popup-cancel-btn">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} className="bulk-upload-popup-action-btn">
          Upload
        </Button>
      </Box>
    </Dialog>
  );
};

export default BulkUploadPopup;
